/**
 * 里程碑代号 → 人话名称 + 是给客户看的还是我们内部管的。
 *
 * 项目概览原来是 13 个代号平铺一列：GATE1 / GATE2 / GATE3 / I_OTS / OTS /
 * LAUNCH / I_PPAP / PPAP / GATE4 / I_SOP / SOP / GATE5 / GATE6。
 * 项目经理要的是**两组**：哪些是对客户承诺的日子，哪些是自己内部卡的
 * （试用反馈 0814，肖娟）。
 *
 * 两个维度刻意分开，别混成一个：
 * - `audience`：客户 / 内部——**谁在等这个日子**。客户节点误期要对外解释。
 * - `isGate`：是不是阶段出口门。GATE3 既是 P03 的出口门，又是客户认的 T0，
 *   两个标都要显示，压成一个标就丢信息。
 */
export type MilestoneAudience = 'customer' | 'internal';

export interface MilestoneMeta {
  code: string;
  title: string;
  audience: MilestoneAudience;
  /** 阶段出口门（GATE1–GATE6） */
  isGate: boolean;
  /** 是这个阶段的出口门；非 Gate 为 null。和后端 milestone-catalog.ts 的 stageCode 同一套 */
  exitOf?: string | null;
  /**
   * 窄地方用的短名（大屏表头，0930）。和 title 是同一个意思，只是去掉括号里的补充说明——
   * 大屏原来自己写了一份「G2 方案」「G4 工装」「G6 移交」，和这里对不上，看的人会误解。
   */
  short?: string;
}

const CATALOG: readonly MilestoneMeta[] = [
  { code: 'GATE1', title: '项目启动', audience: 'internal', isGate: true, exitOf: 'P01', short: '项目启动' },
  { code: 'GATE2', title: '数据冻结（TKO 工检模启动）', audience: 'internal', isGate: true, exitOf: 'P02', short: '数据冻结' },
  // T0 内部试模确认：先内部过，再见客户——和 I_OTS / I_PPAP / I_SOP 一个道理
  { code: 'I_T0', title: 'T0 内部试模确认', audience: 'internal', isGate: false, short: 'T0 确认' },
  // T0 首次试模：客户要来看的节点，同时是 P03 的出口门
  { code: 'GATE3', title: 'T0 首次试模（客户）', audience: 'customer', isGate: true, exitOf: 'P03', short: 'T0 试模' },
  { code: 'I_OTS', title: 'OTS 批准（内部）', audience: 'internal', isGate: false, short: 'OTS 批准' },
  { code: 'OTS', title: 'OTS 提交（客户）', audience: 'customer', isGate: false, short: 'OTS 提交' },
  { code: 'LAUNCH', title: '量产准备', audience: 'internal', isGate: false, short: '量产准备' },
  { code: 'I_PPAP', title: 'PPAP 提交（内部）', audience: 'internal', isGate: false, short: 'PPAP 提交' },
  { code: 'PPAP', title: 'PPAP 提交（客户）', audience: 'customer', isGate: false, short: 'PPAP 提交' },
  { code: 'GATE4', title: 'PPAP 批准', audience: 'internal', isGate: true, exitOf: 'P04', short: 'PPAP 批准' },
  { code: 'I_SOP', title: 'Internal SOP（内部量产）', audience: 'internal', isGate: false, short: '内部量产' },
  { code: 'SOP', title: 'SOP 量产（客户）', audience: 'customer', isGate: false, short: 'SOP 量产' },
  { code: 'GATE5', title: '项目移交', audience: 'internal', isGate: true, exitOf: 'P05', short: '项目移交' },
  { code: 'GATE6', title: '项目关闭', audience: 'internal', isGate: true, exitOf: 'P06', short: '项目关闭' },
];

/**
 * 表头上那一小块标签：阶段门写 G1–G6（GATE3 同时是客户节点，写「G3·客户」），
 * 其余写「客户」或「内部」——看表头就知道这一列是谁在等（0930 大屏）。
 */
export function milestoneTag(code: string | null | undefined): string {
  const meta = milestoneMeta(code);
  const gate = /^GATE(\d)$/u.exec(meta.code)?.[1];
  if (gate) return meta.audience === 'customer' ? `G${gate}·客户` : `G${gate}`;
  return meta.audience === 'customer' ? '客户' : '内部';
}

/** 阶段名：和项目计划、审批流页里的叫法一致；short 给大屏表头这种窄地方 */
export const PHASE_NAMES: Readonly<Record<string, { full: string; short: string }>> = {
  P01: { full: '项目计划与立项', short: '立项' },
  P02: { full: '产品设计与初始过程开发', short: '设计' },
  P03: { full: '产品和过程开发', short: '开发' },
  P04: { full: '产品和过程确认', short: '确认' },
  P05: { full: '批量生产', short: '量产' },
  P06: { full: '持续改进与关闭', short: '关闭' },
};

const BY_CODE = new Map(CATALOG.map((item) => [item.code, item]));
const ORDER = new Map(CATALOG.map((item, index) => [item.code, index]));

/**
 * 里程碑在流程里的先后位置（CATALOG 的顺序就是 APQP 标准顺序）。
 *
 * **不能按计划日期排**：迁移过来的项目日期是乱填的——秦岭 GATE1 是 2026/09/01、
 * GATE2 却是 2026/11/15、GATE3 又回到 2025/12/15，按日期排出来就是
 * 「OTS 批准 → 量产准备 → PPAP 提交 → 项目启动」这种没法看的顺序
 * （0820 P9：EQS 导出的项目节点排序乱，PDM 里新建的正常——
 * 因为新建项目日期是按模板推算的，本来就递增）。
 * 认不出的代号排到最后，不夹在标准节点中间。
 */
export function milestoneOrder(code: string | null | undefined): number {
  const key = String(code ?? '').trim().toUpperCase();
  return ORDER.get(key) ?? Number.MAX_SAFE_INTEGER;
}

/** 认不出的代号一律当内部，宁可少报一个客户节点，也不要误报 */
export function milestoneMeta(code: string | null | undefined): MilestoneMeta {
  const key = String(code ?? '').trim().toUpperCase();
  return BY_CODE.get(key) ?? { code: key || '—', title: '', audience: 'internal', isGate: false };
}

export const MILESTONE_AUDIENCE_LABEL: Readonly<Record<MilestoneAudience, string>> = {
  customer: '客户里程碑',
  internal: '内部里程碑',
};

export const MILESTONE_AUDIENCE_HINT: Readonly<Record<MilestoneAudience, string>> = {
  customer: '对客户承诺的日子，误期要对外解释',
  internal: '我们自己卡的节点，用来保住上面那几个',
};

/**
 * 这个里程碑**落在**哪个阶段（注意不是「它是谁的出口门」）。
 *
 * GATE1-6 各自就是一个阶段的出口门，直接取 exitOf。其余 8 个不是任何阶段的
 * 边界，但确实落在某个阶段里面——I_OTS 在 GATE3(P03) 之后、GATE4(P04) 之前，
 * 所以属于 P04：到 I_OTS 那天该做完的东西，就是 P04 这一段的活。
 * 算法：从自己往后找第一个 Gate，取它的 exitOf。
 *
 * 后端 `apps/api/src/projects/milestone-catalog.ts` 有一份同样的映射，
 * Gate 提醒的数字就是按它数出来的。两边各自的测试写死同一张期望表，
 * 改一边不改另一边就会红。
 */
export function milestoneStage(code: string | null | undefined): string | null {
  const key = String(code ?? '').trim().toUpperCase().replace(/[\s-]/gu, '_');
  const index = CATALOG.findIndex((item) => item.code === key);
  if (index < 0) return null;
  for (let cursor = index; cursor < CATALOG.length; cursor += 1) {
    const stage = CATALOG[cursor].exitOf;
    if (stage) return stage;
  }
  return null;
}
